import AlternateEmailIcon from '@mui/icons-material/AlternateEmail';
import HomeIcon from '@mui/icons-material/Home';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import YouTubeIcon from '@mui/icons-material/YouTube';
import TwitterIcon from '@mui/icons-material/Twitter';
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import React, { useMemo } from 'react';
import Avatar from 'react-avatar';

import evoachDefaultTheme from '../../theme/theme';
import { BaseComponent } from '../Component';

export interface CoachCardComponent extends BaseComponent {
  type: 'objectCard';
  props: CoachCardProps;
}
export interface CoachCardProps {
  coachAvatar: string;
  coachLogo?: string;
  coachName: string;
  coachDescription?: string;
  coachingLabels?: string[];
  linkedInLink?: string;
  youtubeLink?: string;
  instagramLink?: string;
  twitterLink?: string;
  cardWidth?: string;
  cardHeight?: string;
  coachWebsite?: string;
  coachMail?: string;
}
export const CoachCard: React.FC<CoachCardProps> = ({
  coachAvatar,
  coachLogo,
  coachName,
  coachDescription,
  coachingLabels,
  linkedInLink,
  youtubeLink,
  instagramLink,
  twitterLink,
  cardWidth,
  cardHeight,
  coachWebsite,
  coachMail,
}) => {
  const maxHeight = cardHeight ?? '600px';

  const width = cardWidth ?? '100%';
  const maxWidth = cardWidth ?? '50%';

  const gridCols = useMemo(() => {
    let cols = 0;
    if (coachWebsite) cols++;
    if (coachMail) cols++;
    if (linkedInLink) cols++;
    if (youtubeLink) cols++;
    if (instagramLink) cols++;
    if (twitterLink) cols++;

    return '3rem '.repeat(cols);
  }, [coachWebsite]);

  return (
    <Card
      sx={{
        width: 'fit-content',
        minWidth: width,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        overflow: 'hidden',
      }}
    >
      <CardContent
        sx={{
          height: 'auto',
          overflow: 'hidden',
          paddingBottom: 0,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          width="100%"
          style={{
            display: 'grid',
            grid: `100px / 110px ${gridCols} auto`,
          }}
        >
          <Avatar src={coachAvatar} size="100" round={true} color="#AAAAAA" />
          {coachWebsite && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={coachWebsite} target="_blank">
                <HomeIcon sx={{ width: '2rem', height: 'auto' }} />
              </IconButton>
            </Box>
          )}
          {coachMail && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={`mailto:${coachMail}`} target="_blank">
                <AlternateEmailIcon sx={{ width: '2rem', height: 'auto' }} />
              </IconButton>
            </Box>
          )}

          {linkedInLink && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={linkedInLink} target="_blank">
                <LinkedInIcon sx={{ width: '2rem', height: 'auto' }} />
              </IconButton>
            </Box>
          )}
          {youtubeLink && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={youtubeLink} target="_blank">
                <YouTubeIcon sx={{ width: '2rem', height: 'auto' }} />
              </IconButton>
            </Box>
          )}
          {instagramLink && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={instagramLink} target="_blank">
                <InstagramIcon
                  sx={{
                    width: '2rem',
                    height: 'auto',
                  }}
                />
              </IconButton>
            </Box>
          )}
          {twitterLink && (
            <Box sx={{ alignSelf: 'end' }}>
              <IconButton href={twitterLink} target="_blank">
                <TwitterIcon
                  sx={{
                    width: '2rem',
                    height: 'auto',
                  }}
                />
              </IconButton>
            </Box>
          )}
          {coachLogo && (
            <Box style={{ justifySelf: 'end', alignSelf: 'baseline' }}>
              <img
                height={100}
                src={coachLogo}
                style={{
                  aspectRatio: '3/2',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </Stack>
      </CardContent>
      <CardContent
        sx={{
          paddingRight: '10px',
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          {coachName}
        </Typography>
        <Typography
          variant="body1"
          sx={{ maxHeight: '350px', overflow: 'auto' }}
        >
          {coachDescription}
        </Typography>
        {coachingLabels && (
          <Stack
            direction="row"
            alignItems="center"
            width="100%"
            sx={{ marginTop: '16px' }}
          >
            {coachingLabels.map((label, key) => {
              return (
                <Box
                  sx={{
                    backgroundColor: evoachDefaultTheme.palette.primary.light,
                    borderRadius: '10px',
                    padding: '1px 10px',
                    typography: 'body2',
                    color: 'white',
                    width: 'fit-content',
                    marginRight: '10px',
                  }}
                  key={key}
                >
                  {label}
                </Box>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
